/**
 * K4c.2 — the landlord-deposit document categories, with their first consumer.
 *
 * The wire values mirror ``LANDLORD_DEPOSIT_DOCUMENT_CATEGORIES`` in
 * ``backend/app/constants/unit_documents.py`` one for one. K4c.1 reserved the
 * ``landlord_deposit_`` namespace there and validates it on upload, so a value
 * outside this list is a 422 — this file is the UI half of the same contract,
 * not a second opinion about it.
 *
 * K4c.1 deliberately shipped no frontend constant: a list with no consumer is
 * a list nobody lints (this project's craco config replaces
 * `eslint-config-react-app`, so unused symbols pass silently). The select in
 * `LandlordDepositDetailModal` is that consumer, so the list arrives now.
 *
 * WHY THESE LABELS
 * ----------------
 * "Kautionspolice" rather than the backend comment's longer
 * "Kautionspolice / Sicherheit": 26 characters do not sit well in a 375px
 * select, and the shorter form reads as the sibling of the tenant side's
 * "Mietkautionspolice" — which is exactly what it is, one floor up.
 *
 * "Freigabebeleg" rather than "Rückzahlungsbeleg", because
 * ``ALLOWED_LANDLORD_DEPOSIT_TYPES`` is {bank, insurance, cash, none}: a bank
 * depot and an insurance guarantee are RELEASED, not repaid. Same distinction
 * K4x.2 drew for the tenant badges.
 *
 * Order is the dropdown order: the document that establishes the security,
 * then the two that move it, then the honest remainder.
 */

/** `{value, label}` in dropdown order. The four wire values are closed. */
export const LANDLORD_DEPOSIT_DOCUMENT_CATEGORIES = [
  { value: "landlord_deposit_contract", label: "Kautionspolice" },
  { value: "landlord_deposit_payment_proof", label: "Einzahlungsbeleg" },
  { value: "landlord_deposit_return_proof", label: "Freigabebeleg" },
  { value: "landlord_deposit_other", label: "Sonstiges" },
];

/**
 * The four wire values as a Set, for filtering a unit's documents.
 *
 * A unit carries other categories — `rent_contract`, `insurance`, `internet`,
 * `handover`, `other` — and none of them belongs in a deposit view. `insurance`
 * is the one worth naming: it is the general unit category for ANY policy
 * (building, liability, contents) and predates the deposit namespace by years.
 * Showing it here would claim something about data nobody entered as a
 * security document.
 */
export const LANDLORD_DEPOSIT_DOCUMENT_CATEGORY_VALUES = new Set(
  LANDLORD_DEPOSIT_DOCUMENT_CATEGORIES.map((c) => c.value)
);

/** German label for one landlord-deposit category, else the raw value. */
export function landlordDepositDocumentCategoryLabel(category) {
  const key = String(category || "").trim();
  if (!key) return "—";
  const hit = LANDLORD_DEPOSIT_DOCUMENT_CATEGORIES.find((c) => c.value === key);
  return hit ? hit.label : key;
}

/** Whether a unit document belongs in the landlord-deposit view. */
export function isLandlordDepositDocument(doc) {
  return LANDLORD_DEPOSIT_DOCUMENT_CATEGORY_VALUES.has(
    String(doc?.category || "").trim()
  );
}

/* ── Tenant side ─────────────────────────────────────────────────────────── */

/**
 * The three tenancy-document categories a deposit view may show and offer.
 *
 * A subset of `ALLOWED_TENANCY_DOCUMENT_CATEGORIES`, and deliberately NOT the
 * whole list: `rent_contract`, `handover_protocol`, `key_receipt`,
 * `damage_evidence`, `repair_invoice` and `other` are real tenancy documents
 * that have nothing to do with a security. `other` is the one to be careful
 * about — offering it here would turn "I am not sure" into a deposit document.
 *
 * Labels are the repo's own, from `TenancyDocumentsSection`. Restated rather
 * than imported: that module is a 335-line tab section, and importing it into
 * a modal to reach three strings would pull its whole load path along.
 */
export const TENANT_DEPOSIT_DOCUMENT_CATEGORIES = [
  { value: "deposit_contract", label: "Mietkautionspolice" },
  { value: "deposit_payment_proof", label: "Kautions-Einzahlungsbeleg" },
  { value: "deposit_return_proof", label: "Kautions-Rückzahlungsbeleg" },
];

export const TENANT_DEPOSIT_DOCUMENT_CATEGORY_VALUES = new Set(
  TENANT_DEPOSIT_DOCUMENT_CATEGORIES.map((c) => c.value)
);

/** German label for one tenant-deposit category, else the raw value. */
export function tenantDepositDocumentCategoryLabel(category) {
  const key = String(category || "").trim();
  if (!key) return "—";
  const hit = TENANT_DEPOSIT_DOCUMENT_CATEGORIES.find((c) => c.value === key);
  return hit ? hit.label : key;
}

/** Whether a tenancy document belongs in the deposit view. */
export function isTenantDepositDocument(doc) {
  return TENANT_DEPOSIT_DOCUMENT_CATEGORY_VALUES.has(
    String(doc?.category || "").trim()
  );
}
